import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DATE_V3, SELECT_V2 } from '@app/formly/helpers/fields';
import { toISO8601 } from '@app/formly/types/date/date-helpers';
import { RadarFormAbstract } from '@app/shared/components/radar-form-abstract';
import { AgentSubstitution } from '@app/shared/services/agent-substitution/agent-substitution.service';
import {
  AgentSubstitutionTeamService,
  TeamSubstitution,
  TeamSubstitutionAgent,
  includeOwnCoverage,
  mapTeamAgentToDropdown,
  toSubstitutionDateTime,
  toSubstitutionEndDateTime,
} from '@app/shared/services/agent-substitution/agent-substitution-team.service';
import { Tab, DropdownItem } from '@voyagu/radar-design-system';
import { CognitoAuthService } from '@auth/services/cognito-auth.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

enum CoverageTab {
  Setup = 'setup',
  Active = 'active',
}

type OwnCoverageView = {
  id: string;
  coveringName: string;
  startLabel: string;
  endLabel: string;
};

/**
 * What the graphql layer hands back for a `Date` scalar. Codegen types `startDate` /
 * `throughDate` as `Date` (`node-scripts/set-codegen.ts`) and nothing parses scalars, so the
 * value is the raw `YYYY-MM-DD` string the backend printed — in the absent agent's shift zone.
 * This states that, it does not convert: putting the string through `new Date()` would make it
 * UTC midnight and render it a day early for any viewer west of that zone. House precedent for
 * the same mismatch: `src/app/pages/agency-parameters/agency-parameters.utils.ts:30-36`.
 */
const servedDate = (value?: Date | null): string => (value as string | undefined) ?? '';

@Component({
  selector: 'team-lead-substitution-form',
  templateUrl: './team-lead-substitution-form.component.html',
  styleUrls: ['./team-lead-substitution-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamLeadSubstitutionFormComponent extends RadarFormAbstract implements OnInit {
  tabs: Tab[] = [
    { icon: '', label: 'Set up coverage', value: CoverageTab.Setup },
    { icon: '', label: 'Active coverages', value: CoverageTab.Active },
  ];
  activeTab: Tab = this.tabs[0];
  CoverageTab = CoverageTab;

  teamAgents: TeamSubstitutionAgent[] = [];
  activeCoverages: TeamSubstitution[] = [];
  ownCoverage: OwnCoverageView | null = null;
  expandedId: string | null = null;

  loading = true;
  submitting = false;
  loadingActive = false;

  coveringAgents$ = new BehaviorSubject<DropdownItem[]>([]);

  ngOnInit() {
    super.ngOnInit();
    this.setCreateFields();
    this.loadTeamAgents();
    this.loadOwnCoverage();
  }

  selectTab(tab: Tab) {
    this.activeTab = tab;
    if (tab.value === String(CoverageTab.Active)) {
      this.loadActiveCoverages();
    }
    this.cdRef.markForCheck();
  }

  loadTeamAgents() {
    this.loading = true;
    this.cdRef.markForCheck();

    this.teamService
      .getTeamAgents()
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe((agents) => {
        this.teamAgents = agents;
        this.refreshCoveringOptions();
        this.cdRef.markForCheck();
      });
  }

  loadOwnCoverage() {
    this.teamService
      .getOwnActive()
      .pipe(finalize(() => this.cdRef.markForCheck()))
      .subscribe((own) => {
        this.applyOwnCoverage(own);
      });
  }

  loadActiveCoverages() {
    this.loadingActive = true;
    this.cdRef.markForCheck();

    forkJoin({
      rows: this.teamService.getActiveTeamSubstitutions(),
      own: this.teamService.getOwnActive(),
    })
      .pipe(finalize(() => this.setLoadingActive(false)))
      .subscribe(({ rows, own }) => {
        this.applyOwnCoverage(own);
        this.activeCoverages = includeOwnCoverage(rows, own, this.currentUserAsAbsent());
        if (this.expandedId && !this.activeCoverages.some((row) => row.id === this.expandedId)) {
          this.expandedId = null;
        }
        this.cdRef.markForCheck();
      });
  }

  applyOwnCoverage(own: AgentSubstitution | null) {
    if (!own?.id) {
      this.ownCoverage = null;
      return;
    }

    // `OwnCoverageView`'s labels are rendered strings already, so the served dates go in as they
    // arrive — `servedDate` only restates the scalar's runtime type, it converts nothing.
    this.ownCoverage = {
      id: own.id,
      coveringName: own.coveringAgent?.fullName || 'No covering agent',
      startLabel: servedDate(own.startDate),
      endLabel: servedDate(own.throughDate),
    };
  }

  currentUserAsAbsent() {
    const user = this.authService.user;
    if (!user?.id) {
      return null;
    }

    return {
      id: user.id,
      fullName: user.fullName || '',
      picture: user.picture,
    };
  }

  setCreateFields() {
    const absentAgents$ = this.teamService
      .getTeamAgents()
      .pipe(map((agents) => agents.map(mapTeamAgentToDropdown)));

    this.fields = [
      SELECT_V2(
        {
          key: 'absentAgentId',
          props: {
            label: 'Who needs coverage?',
            required: true,
          },
          hooks: {
            onInit: (field) => {
              field.formControl?.valueChanges.subscribe((absentId: string) => {
                this.onAbsentChanged(absentId);
              });
            },
          },
        },
        absentAgents$,
      ),
      SELECT_V2(
        {
          key: 'coveringAgentId',
          props: {
            label: 'Who will cover the absence?',
            required: true,
          },
          expressions: {
            'props.disabled': (field: FormlyFieldConfig) => !field.model?.absentAgentId,
          },
        },
        this.coveringAgents$.asObservable(),
      ),
      DATE_V3({
        key: 'startAt',
        props: {
          label: 'Start date',
          required: true,
        },
        defaultValue: toISO8601(new Date()),
        expressions: {
          'props.disabled': (field: FormlyFieldConfig) => !field.model?.absentAgentId,
        },
      }),
      DATE_V3({
        key: 'endAt',
        props: {
          label: 'End date',
          placeholder: 'No end date',
          required: false,
        },
        expressions: {
          'props.disabled': (field: FormlyFieldConfig) => !field.model?.absentAgentId,
        },
      }),
    ];

    this.formModel = {
      absentAgentId: null,
      coveringAgentId: null,
      startAt: toISO8601(new Date()),
      endAt: null,
    };
  }

  onAbsentChanged(absentId: string | null) {
    this.refreshCoveringOptions(absentId);
    if (
      this.formModel.coveringAgentId &&
      String(this.formModel.coveringAgentId) === String(absentId)
    ) {
      this.formModel = { ...this.formModel, coveringAgentId: null };
      this.formGroup.patchValue({ coveringAgentId: null });
    }
    this.cdRef.markForCheck();
  }

  refreshCoveringOptions(absentId?: string | null) {
    const excluded = absentId ?? this.formModel?.absentAgentId;
    const options = this.teamAgents
      .filter((agent) => String(agent.id) !== String(excluded))
      .map(mapTeamAgentToDropdown);
    this.coveringAgents$.next(options);
  }

  startCoverage() {
    if (!this.formGroup.valid || this.submitting) {
      return;
    }

    const model = this.formModel;
    this.submitting = true;

    this.teamService
      .create({
        absentAgentId: model.absentAgentId as string,
        coveringAgentId: model.coveringAgentId as string,
        startAt: new Date(toSubstitutionDateTime(model.startAt as string)),
        endAt: model.endAt ? new Date(toSubstitutionEndDateTime(model.endAt as string)) : undefined,
      })
      .pipe(finalize(() => this.setSubmitting(false)))
      .subscribe(() => {
        this.resetCreateForm();
        this.selectTab(this.tabs[1]);
      });
  }

  toggleRow(id?: string) {
    if (!id) {
      return;
    }

    this.expandedId = this.expandedId === id ? null : id;
    this.cdRef.markForCheck();
  }

  endCoverage(row: TeamSubstitution) {
    this.endCoverageById(row.id);
  }

  endOwnCoverage() {
    this.endCoverageById(this.ownCoverage?.id);
  }

  endCoverageById(id?: string) {
    if (!id || this.submitting) {
      return;
    }

    this.submitting = true;
    this.teamService
      .end(id)
      .pipe(finalize(() => this.setSubmitting(false)))
      .subscribe(() => {
        this.activeCoverages = this.activeCoverages.filter((item) => item.id !== id);
        if (this.ownCoverage?.id === id) {
          this.ownCoverage = null;
        }
        if (this.expandedId === id) {
          this.expandedId = null;
        }
        this.cdRef.markForCheck();
      });
  }

  resetCreateForm() {
    this.formModel = {
      absentAgentId: null,
      coveringAgentId: null,
      startAt: toISO8601(new Date()),
      endAt: null,
    };
    this.formGroup.reset(this.formModel);
    this.formGroup.markAsPristine();
    this.refreshCoveringOptions(null);
    this.cdRef.markForCheck();
  }

  setLoading(loading: boolean) {
    this.loading = loading;
    this.cdRef.markForCheck();
  }

  setLoadingActive(loading: boolean) {
    this.loadingActive = loading;
    this.cdRef.markForCheck();
  }

  setSubmitting(submitting: boolean) {
    this.submitting = submitting;
    this.cdRef.markForCheck();
  }

  constructor(
    private teamService: AgentSubstitutionTeamService,
    private authService: CognitoAuthService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }
}
